"use client";
import React, { useEffect, useMemo } from "react";
import {
  SongwriterBlock,
  SongwriterBlockType,
  songwriterToolbarTypes,
  songwriterIcons,
  songwriterStyles,
} from "../types/songwriter.types";
import { comparePositions } from "../service/songwriter.service";
import {
  Save,
  Sparkles,
  Loader2,
  Lock,
  Unlock,
  Layers,
  ChevronLeft,
} from "lucide-react";

interface TooltipProps {
  children: React.ReactNode;
  content: string;
  shortcut?: string;
  align?: "center" | "left" | "right";
}

function Tooltip({
  children,
  content,
  shortcut,
  align = "center",
}: TooltipProps) {
  const alignClasses = {
    center: "left-1/2 -translate-x-1/2 items-center",
    left: "left-0 items-start",
    right: "right-0 items-end",
  };

  const arrowClasses = {
    center: "",
    left: "left-3.5",
    right: "right-3.5",
  };

  return (
    <div className="group relative flex items-center justify-center">
      {children}
      <div
        className={`absolute top-[calc(100%+8px)] scale-95 opacity-0 pointer-events-none group-hover:scale-100 group-hover:opacity-100 transition-all duration-150 ease-out z-[100] flex flex-col ${alignClasses[align]}`}
      >
        <div
          className={`w-2 h-2 rotate-45 bg-blumine-950 border-l border-t border-white/10 -mb-1 shadow-sm ${align === "center" ? "" : "relative"} ${arrowClasses[align]}`}
        />
        <div className="bg-blumine-950/95 text-white text-[11px] font-medium px-2.5 py-1.5 rounded-lg shadow-xl border border-white/10 backdrop-blur-sm whitespace-nowrap flex items-center gap-1.5 font-sans">
          <span>{content}</span>
          {shortcut && (
            <kbd className="bg-blumine-800 text-[10px] text-blumine-300 px-1.5 py-0.5 rounded border border-zinc-700 font-sans font-semibold">
              {shortcut}
            </kbd>
          )}
        </div>
      </div>
    </div>
  );
}

interface SongwriterEditorViewProps {
  blocks: SongwriterBlock[];
  isLoading: boolean;
  isLocked?: boolean;
  handleToggleLock?: () => void;
  refs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  focusedBlockId: string;
  autosaveStatus: "idle" | "typing" | "saving" | "saved" | "error";
  handleBlockTextChange: (id: string, text: string) => void;
  handleBlockTypeChange: (newType: SongwriterBlockType) => void;
  handleKeyDown: (
    e: React.KeyboardEvent<HTMLDivElement>,
    id: string,
    text: string,
  ) => void;
  handleFocus: (id: string) => void;
  handleSelect: (id: string) => void;
  exportToTxt: () => void;
  onBack?: () => void;
}

export function SongwriterEditorView({
  blocks,
  isLoading,
  isLocked = false,
  handleToggleLock,
  refs,
  focusedBlockId,
  autosaveStatus,
  handleBlockTextChange,
  handleBlockTypeChange,
  handleKeyDown,
  handleFocus,
  handleSelect,
  exportToTxt,
  onBack,
}: SongwriterEditorViewProps) {
  const COURIER_STACK = "'Courier Prime', 'Courier', monospace";

  // Full-screen Premium Loader - Remains active until draft is parsed and hydrated
  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-zinc-50 font-sans">
        <div className="flex flex-col items-center gap-4 animate-in fade-in zoom-in duration-300">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-indigo-600 animate-pulse">
            Loading Lyrical Editor...
          </p>
        </div>
      </div>
    );
  }

  // Force Google Font Courier Prime integration for standard typewriter presentation
  useEffect(() => {
    const fontId = "google-font-courier-prime";
    const styleId = "screenplay-editor-force-v4";

    if (!document.getElementById(fontId)) {
      const link = document.createElement("link");
      link.id = fontId;
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Courier+Prime:ital,wght@0,400;0,700;1,400;1,700&display=swap";
      document.head.appendChild(link);
    }

    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        [data-screenplay-editor] *, 
        [data-screenplay-editor] div, 
        [data-screenplay-editor] span, 
        [data-screenplay-editor] [contenteditable="true"] {
          font-family: ${COURIER_STACK} !important;
          -webkit-font-smoothing: antialiased;
        }
      `;
      document.head.appendChild(style);
    }
  }, [COURIER_STACK]);

  const sortedBlocks = useMemo(() => {
    return [...blocks].sort((a, b) => comparePositions(a.position, b.position));
  }, [blocks]);

  return (
    <>
      {/* Top Header Navigation Bar - Identical to Screenplay Editor Top Bar Layout */}
      <div className="sticky top-0 left-0 right-0 z-50 w-full bg-zinc-50/95 backdrop-blur-md border-b border-zinc-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.01)] flex flex-wrap lg:flex-nowrap items-center justify-between px-3 sm:px-6 py-2 mb-8 select-none transition-all gap-y-2">
        {/* Left Section: Branding & Saved Status */}
        <div className="flex items-center gap-3 w-auto shrink-0 order-1">
          {onBack && (
            <button
              onClick={onBack}
              className="flex items-center justify-center p-1.5 rounded-lg hover:bg-zinc-200/50 text-zinc-600 hover:text-zinc-900 transition-all duration-150 active:scale-95"
              aria-label="Go back"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          )}
          <span className="text-[11px] font-bold tracking-wider text-blumine-400/80 uppercase font-sans">
            Lyrical Editor
          </span>
          {/* Debounced Autosave Badges matching Screenplay Editor statuses */}
          {autosaveStatus === "saved" && (
            <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-100/50 animate-in fade-in duration-300 font-sans">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[10px] font-semibold tracking-wide">
                Synced
              </span>
            </div>
          )}
          {autosaveStatus === "saving" && (
            <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100/50 animate-in fade-in duration-300 font-sans">
              <Loader2 className="w-2.5 h-2.5 animate-spin text-blue-500" />
              <span className="text-[10px] font-semibold tracking-wide">
                Saving...
              </span>
            </div>
          )}
          {autosaveStatus === "typing" && (
            <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-100/50 animate-in fade-in duration-300 font-sans">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
              <span className="text-[10px] font-semibold tracking-wide">
                Drafting...
              </span>
            </div>
          )}
          {autosaveStatus === "error" && (
            <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-rose-50 text-rose-600 border border-rose-100/50 animate-in fade-in duration-300 font-sans">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
              <span className="text-[10px] font-semibold tracking-wide">
                Error Saving
              </span>
            </div>
          )}
        </div>

        {/* Center Section: Centered Formatting Buttons (Direct Row Layout) */}
        <div className="flex items-center justify-center gap-1 sm:gap-2 basis-full lg:basis-auto flex-initial min-w-0 py-1 lg:py-0.5 order-3 lg:order-2">
          {songwriterToolbarTypes.map((type) => {
            const selected =
              blocks.find((b) => b.id === focusedBlockId)?.type === type;
            return (
              <Tooltip
                key={type}
                content={songwriterStyles[type].label}
                shortcut="Tab / Enter"
              >
                <button
                  type="button"
                  disabled={isLocked}
                  onClick={() => handleBlockTypeChange(type)}
                  className={`w-8 h-8 sm:w-9 sm:h-9 flex shrink-0 items-center justify-center rounded-lg transition-all duration-150 ease-out active:scale-95 whitespace-nowrap ${
                    selected
                      ? "bg-blumine-200/60 text-blumine-800 shadow-sm border border-blumine-200/50"
                      : "text-blumine-400 hover:bg-blumine-100 hover:text-blumine-700"
                  } ${isLocked ? "opacity-50 cursor-not-allowed pointer-events-none" : ""}`}
                >
                  <div
                    className={`flex items-center justify-center transition-all duration-150 ${selected ? "scale-105 opacity-100" : "opacity-80 hover:opacity-100"}`}
                  >
                    {songwriterIcons[type]}
                  </div>
                </button>
              </Tooltip>
            );
          })}
        </div>

        {/* Right Section: Utilities (Export & Lock) */}
        <div className="flex items-center justify-end gap-1 sm:gap-1.5 w-auto lg:w-[220px] shrink-0 order-2 lg:order-3">
          {/* Lock / Unlock Toggle Button */}
          {handleToggleLock && (
            <Tooltip
              content={isLocked ? "Unlock Editor" : "Lock Editor"}
              align="right"
            >
              <button
                onClick={handleToggleLock}
                className={`w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg transition active:scale-95 ${
                  isLocked
                    ? "text-rose-500 hover:bg-rose-50 hover:text-rose-700 bg-rose-50/50"
                    : "text-blumine-400 hover:bg-blumine-100 hover:text-blumine-700"
                }`}
              >
                {isLocked ? (
                  <Lock
                    className="w-4 h-4 sm:w-[18px] sm:h-[18px]"
                    strokeWidth={1.5}
                  />
                ) : (
                  <Unlock
                    className="w-4 h-4 sm:w-[18px] sm:h-[18px]"
                    strokeWidth={1.5}
                  />
                )}
              </button>
            </Tooltip>
          )}

          {/* Download Draft Icon Button */}
          <Tooltip content="Export lyric sheet (.txt)" align="right">
            <button
              onClick={exportToTxt}
              className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg text-blumine-400 hover:bg-blumine-100 hover:text-blumine-700 transition active:scale-95"
            >
              <Save
                className="w-4 h-4 sm:w-[18px] sm:h-[18px]"
                strokeWidth={1.5}
              />
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Editor Composition Canvas Area */}
      <div className="flex flex-col gap-12 pb-24 w-full items-center justify-center px-4 sm:px-6 md:px-8">
        <div
          className="relative bg-[#fdfdfc] shadow-2xl shadow-zinc-300/60 border border-zinc-100 rounded-sm md:rounded-md pl-[1.5in] py-[1in] pr-[1in] flex flex-col w-[210mm] min-h-[297mm] shrink-0"
          style={{
            paddingLeft: "1.5in",
            paddingRight: "1in",
            paddingTop: "1in",
            paddingBottom: "1in",
            lineHeight: "1.2",
          }}
          data-screenplay-editor="true"
        >
          {sortedBlocks.map((block) => {
            const isFocused = focusedBlockId === block.id;
            const styleConfig = songwriterStyles[block.type];

            return (
              <div
                key={block.id}
                data-block-id={block.id}
                className={`relative rounded-sm transition-all duration-200 outline-none ${
                  isFocused ? "bg-zinc-100/50" : "bg-transparent"
                }`}
                data-block-type={block.type}
              >
                <div
                  ref={(el) => {
                    if (!el) return;
                    refs.current[block.id] = el;
                  }}
                  contentEditable={!isLocked}
                  suppressContentEditableWarning
                  aria-label={`${styleConfig.label} text`}
                  aria-multiline={block.type === "SONG_LYRICS"}
                  spellCheck={false}
                  {...({ placeholder: styleConfig.placeholder } as any)}
                  className={`block outline-none w-full min-h-[2.5rem] px-4 py-2 break-words songwriter-placeholder-container ${styleConfig.className} ${
                    block.text.trim() === "" ? "is-empty" : ""
                  }`}
                  style={styleConfig.inputStyle}
                  onInput={(e) =>
                    handleBlockTextChange(
                      block.id,
                      (e.target as HTMLDivElement).innerText,
                    )
                  }
                  onKeyDown={(e) => handleKeyDown(e, block.id, block.text)}
                  onFocus={() => {
                    handleFocus(block.id);
                    handleSelect(block.id);
                  }}
                  onKeyUp={() => handleSelect(block.id)}
                  onMouseUp={() => handleSelect(block.id)}
                  onSelect={() => handleSelect(block.id)}
                >
                  {/* DO NOT PASS CHILDREN TO PREVENT REACT FROM OVERWRITING TEXT AND RESETTING CARETS IN REVERSE */}
                </div>
              </div>
            );
          })}

          {/* Clean Composer Footer */}
          <div className="mt-auto pt-16 flex flex-col items-center gap-2 border-t border-zinc-200/40 border-dashed select-none text-zinc-300 font-sans">
            <Sparkles className="w-4 h-4 text-zinc-400" />
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-400">
              End of Song Sheet
            </p>
          </div>
        </div>
      </div>

      {/* Placeholder rule for empty contenteditable elements */}
      <style>{`
        .songwriter-placeholder-container.is-empty::before {
          content: attr(placeholder);
          color: #a1a1aa; /* text-zinc-400 */
          font-style: italic;
          font-weight: normal;
          letter-spacing: normal;
          text-transform: none;
          pointer-events: none;
          display: inline-block;
        }
      `}</style>
    </>
  );
}
